import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ActionIconButton } from '@onesource/components';
import { isTeacherReviewedFormType, type TContentBlockBase } from '@onesource/schemas';
import { type TFormSubmission, type TFormWithQuestions, fetchFormSubmission, getMediaUrl } from '@onesource/services';
import DOMPurify from 'dompurify';
import { useState } from 'react';
import {
  MdCheckCircle,
  MdFileDownload,
  MdHourglassEmpty,
  MdOutlineAssignment,
  MdPlayArrow,
  MdBlock,
  MdRefresh,
  MdSchedule,
} from 'react-icons/md';

import { SubmissionFeedback } from '../form-review/submission-feedback';
import { FORM_BODY_MAX_WIDTH } from './rich-content-sx';
import { downloadSubmissionPdf } from './submission-pdf';
import { SubmissionStatusChip } from './submission-status-chip';

interface FormReceptionProps {
  form: TFormWithQuestions;
  formBlock: TContentBlockBase;
  userSubmissions: TFormSubmission[] | undefined;
  isLoadingSubmissions: boolean;
  onStart: () => void;
  onResume: (submissionId: string) => void;
  isStarting: boolean;
  isResuming: boolean;
  spaceName?: string;
  /** Printed on the workbook cover's "Name" rule. */
  memberName?: string;
}

const PRIMARY_BUTTON_SX = {
  bgcolor: '#ff4d6d',
  color: 'white',
  width: { xs: '100%', sm: 'auto' },
  minHeight: 44,
  px: 3,
  py: 1.25,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': { bgcolor: '#e63956' },
} as const;

const formatDate = (value: string | null) => {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const FormReception = ({
  form,
  formBlock,
  userSubmissions,
  isLoadingSubmissions,
  onStart,
  onResume,
  isStarting,
  isResuming,
  spaceName,
  memberName,
}: FormReceptionProps) => {
  const theme = useTheme();
  // Phones get an icon-only PDF action and one date per submission, so the card header fits one line.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadPdf = async (submissionId: string) => {
    setDownloadingId(submissionId);
    try {
      const detail = await fetchFormSubmission(submissionId);
      await downloadSubmissionPdf({
        submission: detail,
        formTitle: form.title,
        questions: form.questions ?? [],
        groups: form.groups ?? [],
        spaceName,
        formType: form.formType,
        memberName,
        formDescription: form.description,
        coverEnabled: form.pdfCoverEnabled,
        coverKicker: form.pdfCoverKicker,
        coverSubtitle: form.pdfCoverSubtitle,
      });
    } catch (error) {
      console.error('Failed to download submission PDF:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const bannerSource = form.bannerUrl || formBlock.textBannerUrl;
  const bannerUrl = bannerSource ? getMediaUrl(bannerSource) : undefined;

  const submissions = [...(userSubmissions ?? [])].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );
  const inProgressSubmission = submissions.find(s => s.status === 'in_progress');
  const submittedCount = submissions.filter(s => s.status === 'submitted').length;
  // A submission the teacher rejected is to be done again, so it doesn't use up the one submission.
  const activeSubmittedCount = submissions.filter(s => s.status === 'submitted' && !s.rejectedAt).length;
  // A Hands-On Practice is reviewed once per student, so it is always single-submission.
  const isTeacherReviewed = isTeacherReviewedFormType(form.formType);
  const blockedByMultipleSubmissions =
    (!form.allowMultipleSubmissions || isTeacherReviewed) && activeSubmittedCount > 0;
  // The newest entry was rejected and nothing replaces it yet: the student owes a new submission.
  const mustResubmit = isTeacherReviewed && !!submissions[0]?.rejectedAt;
  const hasFeedback = submissions.some(s => !!s.feedbackSubmittedAt);
  // The newest submission with feedback opens with it showing; older ones stay folded.
  const newestReviewedId = submissions.find(s => !!s.feedbackSubmittedAt)?.id;
  const questionCount = form.questions?.length ?? 0;

  const renderPrimaryAction = () => {
    if (isLoadingSubmissions) {
      return <CircularProgress size={28} />;
    }

    if (inProgressSubmission) {
      return (
        <Button
          startIcon={isResuming ? <CircularProgress size={18} color="inherit" /> : <MdRefresh size={20} />}
          onClick={() => onResume(inProgressSubmission.id)}
          disabled={isResuming || isStarting}
          variant="contained"
          sx={{ ...PRIMARY_BUTTON_SX }}
        >
          Resume Submission
        </Button>
      );
    }

    if (blockedByMultipleSubmissions) {
      return null;
    }

    return (
      <Button
        startIcon={isStarting ? <CircularProgress size={18} color="inherit" /> : <MdPlayArrow size={20} />}
        onClick={onStart}
        disabled={isStarting || isResuming || questionCount === 0}
        variant="contained"
        sx={{ ...PRIMARY_BUTTON_SX, '&:disabled': { bgcolor: '#ccc', color: '#666' } }}
      >
        {mustResubmit ? 'Submit again' : submittedCount > 0 ? 'Start New Submission' : 'Start Form'}
      </Button>
    );
  };

  return (
    <Box maxWidth={FORM_BODY_MAX_WIDTH} mx="auto" sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          borderRadius: '15px',
          overflow: 'hidden',
          bgcolor: 'white',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            // Without an image the band only carries colour, so it stays short — the title is what matters.
            ...(bannerUrl ? { paddingTop: '28.33%' } : { height: { xs: 72, md: 112 } }),
            background: bannerUrl
              ? `url(${bannerUrl}) center/cover no-repeat`
              : 'linear-gradient(135deg, #ff4d6d 0%, #ff8fa3 100%)',
          }}
        >
          {!bannerUrl && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component={MdOutlineAssignment}
                sx={{ fontSize: { xs: 36, md: 56 }, color: 'rgba(255,255,255,0.9)' }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: 'Georgia, serif', fontWeight: 500, fontSize: { xs: '1.5rem', md: '2rem' }, mb: 1.5 }}
          >
            {form.title}
          </Typography>

          {form.description && (
            <Typography
              component="div"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                fontSize: '0.95rem',
                mb: 3,
                '& p': { m: 0, mb: 1 },
                '& ul, & ol': { pl: 3, m: 0, mb: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.description) }}
            />
          )}

          {questionCount === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This form has no questions yet.
            </Alert>
          )}

          {mustResubmit && !inProgressSubmission && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your teacher asked you to submit this form again. Read their reason below, then start a new submission.
            </Alert>
          )}

          {blockedByMultipleSubmissions && !inProgressSubmission && (
            <Alert severity={isTeacherReviewed && hasFeedback ? 'success' : 'info'} sx={{ mb: 3 }}>
              {!isTeacherReviewed
                ? 'You have already submitted this form. Multiple submissions are not allowed.'
                : hasFeedback
                  ? 'Your teacher has reviewed your submission. Read their feedback below.'
                  : 'Your submission is with your teacher for review. You’ll be notified when their feedback is ready.'}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>{renderPrimaryAction()}</Box>
        </Box>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1.1rem' }}>
          Your Submissions
        </Typography>

        {isLoadingSubmissions ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : submissions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You have not submitted this form yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {submissions.map(submission => {
              const isInProgress = submission.status === 'in_progress';
              const showsReview = isTeacherReviewed && !isInProgress;
              return (
                <Box
                  key={submission.id}
                  sx={{
                    p: { xs: 1.75, sm: 2 },
                    borderRadius: '12px',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: isInProgress ? '#ffd1da' : theme.palette.divider,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                      {isInProgress ? (
                        <MdHourglassEmpty size={22} color="#ff4d6d" style={{ flexShrink: 0 }} />
                      ) : showsReview && submission.rejectedAt ? (
                        <MdBlock size={22} color="#c62828" style={{ flexShrink: 0 }} />
                      ) : showsReview && !submission.feedbackSubmittedAt ? (
                        <MdSchedule size={22} color="#b45309" style={{ flexShrink: 0 }} />
                      ) : (
                        <MdCheckCircle size={22} color="#4caf50" style={{ flexShrink: 0 }} />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
                        >
                          Submission #{submission.submissionNumber}
                          <SubmissionStatusChip submission={submission} isTeacherReviewed={isTeacherReviewed} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                          {isPhone && submission.submittedAt
                            ? `Submitted ${formatDate(submission.submittedAt)}`
                            : `Started ${formatDate(submission.startedAt)}`}
                          {!isPhone && submission.submittedAt && ` · Submitted ${formatDate(submission.submittedAt)}`}
                        </Typography>
                      </Box>
                    </Box>

                    {isInProgress ? (
                      <Button
                        size="small"
                        onClick={() => onResume(submission.id)}
                        disabled={isResuming || isStarting}
                        sx={{
                          bgcolor: '#ff4d6d',
                          color: 'white',
                          px: 2,
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#e63956' },
                        }}
                      >
                        Resume
                      </Button>
                    ) : isPhone ? (
                      <ActionIconButton
                        tooltip="Download PDF"
                        aria-label="Download PDF"
                        icon={
                          downloadingId === submission.id ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <MdFileDownload size={20} />
                          )
                        }
                        onClick={() => handleDownloadPdf(submission.id)}
                        disabled={downloadingId === submission.id}
                        sx={{
                          width: 44,
                          height: 44,
                          flexShrink: 0,
                          color: '#ff4d6d',
                          bgcolor: 'rgba(255,77,109,0.08)',
                        }}
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          downloadingId === submission.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <MdFileDownload size={16} />
                          )
                        }
                        onClick={() => handleDownloadPdf(submission.id)}
                        disabled={downloadingId === submission.id}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#ff4d6d',
                          color: '#ff4d6d',
                          '&:hover': { borderColor: '#e63956', bgcolor: 'rgba(255,77,109,0.06)' },
                        }}
                      >
                        Download PDF
                      </Button>
                    )}
                  </Box>

                  {/* Hands-On Practice: what happens to this entry next, and the teacher's
                    feedback once it is in — aligned with the text above, past the icon. */}
                  {showsReview && (
                    <Box sx={{ pl: { xs: 0, sm: '34px' }, mt: { xs: 1.5, sm: 1 } }}>
                      {submission.rejectedAt ? (
                        <Box
                          sx={{
                            bgcolor: '#fdecee',
                            borderLeft: '3px solid #c62828',
                            borderRadius: '10px',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>
                            Your teacher asked you to submit again
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                            {submission.rejectionReason}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            Rejected {formatDate(submission.rejectedAt)}
                          </Typography>
                        </Box>
                      ) : submission.feedbackSubmittedAt ? (
                        <SubmissionFeedback
                          submissionId={submission.id}
                          defaultOpen={submission.id === newestReviewedId}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Your teacher will review this submission. You&apos;ll be notified when feedback is ready.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider sx={{ mt: 4 }} />
    </Box>
  );
};
